/* eslint-disable @typescript-eslint/no-use-before-define */
import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import styled from 'styled-components';
import moment from 'moment';
import {
  Card,
  Table,
  Link,
  Text,
  theme,
  message,
} from '@brokerbay/design-components';
import { ColumnsType } from 'antd/lib/table';
import {
  getInvoices as getLegacyInvoices,
  InvoiceData,
  InvoicesInfo,
} from 'api/BillingController';
import type { KeyholderInvoiceData } from '@/app-package';
import { featureEnabled, formatCurrency, capitalize } from 'utils';
import sendSupraBillingHistoryEmail from 'api/SupraController/sendSupraBillingHistoryEmail';
import { InvoiceSearchFilters } from './types';
import { getKeyholderInvoices } from '../../../../../api/SupraController';
import Extra from './Extra';
import ManageRecipientModal from './ManageRecipientModal';
import LoadMore from './LoadMore';
import StatusFilterDropdown from './StatusFilterDropdown';
import { BillingInvoicesPageContext } from '../../BillingInvoicesPageContext';

export type InvoicesCardProps = { orgId: string; userId?: string };

type KeyholderInvoiceDataWithURL = KeyholderInvoiceData & { url: string };

// The actual component for this file
const InvoicesCard: React.FC<InvoicesCardProps> = ({ orgId, userId }) => {
  // Define all the useStates and variables we will need
  const [isManageRecipientsOpen, setManageRecipientsOpen] = useState(false);
  const [invoicesData, setInvoicesData] = useState<
    InvoicesInfo['invoices'] | null
  >(null);
  const [email, setEmail] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<
    InvoiceData[] | KeyholderInvoiceDataWithURL[] | []
  >([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [sendingBillingHistoryEmail, setSendingBillingHistoryEmail] =
    useState(false);

  const [filters, setFilters] = useState<InvoiceSearchFilters>({
    status: undefined,
    createdAfter: undefined,
    createdBefore: undefined,
    getSubscriptionType: true,
  });

  const { billingProfile } = useContext(BillingInvoicesPageContext);
  const keyholderBillingEnabled = featureEnabled('keyholderBillingFeature');

  // The format of the columns we will display
  // (Once Stripe is removed we can move this out of the InvoicesCard
  // and it won't need to be a useMemo.)
  const columns = useMemo<ColumnsType<InvoiceData>>(() => {
    const columnsToDisplay: ColumnsType<InvoiceData> = [
      {
        key: 'createdAt',
        dataIndex: 'createdAt',
        width: 130,
        align: 'center',
        render: (createdAt: Date) => (
          <StyledDateCell>
            {moment(createdAt).format('MMM D, YYYY')}
          </StyledDateCell>
        ),
        title: 'Date',
      },
      {
        key: 'invoiceNumber',
        dataIndex: 'invoiceNumber',
        render: (invoiceNumber: string) => `#${invoiceNumber}`,
        title: 'Invoice No.',
        width: 130,
        align: 'center',
      },
      {
        key: 'subscriptionType',
        dataIndex: 'subscriptionType',
        title: 'Invoice Type',
        render: (subscriptionType: string) => {
          if (subscriptionType === 'answering') {
            return 'Live Concierge';
          }
          if (subscriptionType === 'keyholder') {
            return 'Supra Key';
          }
          return 'BrokerBay Showing System';
        },
        width: 130,
        align: 'center',
      },
      {
        key: 'status',
        dataIndex: 'status',
        width: 120,
        align: 'center',
        render: (status: string) => (
          <StyledStatus $status={status}>{capitalize(status)}</StyledStatus>
        ),
        title: 'Status',

        filterDropdown: StatusFilterDropdown,
      },
      // When Stripe removal happens, rename this key 'totalInvoiceAmount' or something
      {
        key: 'amount',
        dataIndex: 'amount',
        width: 110,
        align: 'center',
        render: (input: number) => (input ? formatCurrency(input) : '$0.00'),
        // Title is 'Amount Due' to match the legacy Supra app's column names.
        // In reality, this is the total amount this invoice was billed for.
        title: 'Amount Due',
      },
    ];
    // When Stripe removal happens, this can be merged straight into
    // the above array creation, without the if & push.
    if (keyholderBillingEnabled) {
      columnsToDisplay.push(
        {
          key: 'amountPaid',
          dataIndex: 'amountPaid',
          width: 110,
          align: 'center',
          render: (input: number) => (input ? formatCurrency(input) : '$0.00'),
          title: 'Amount Paid',
        },
        {
          // This is the current outstanding amount the user still owes.
          key: 'amountDue',
          dataIndex: 'amountDue',
          width: 110,
          align: 'center',
          render: (input: number) => (input ? formatCurrency(input) : '$0.00'),
          title: 'Balance',
        },
      );
    }

    // We want this to always be the final column
    columnsToDisplay.push({
      key: 'url',
      dataIndex: 'url',
      width: 80,
      align: 'center',
      render: (url: string) =>
        url && (
          <Link href={url} target="_blank" rel="noopener">
            View
          </Link>
        ),
    });

    return columnsToDisplay as ColumnsType<InvoiceData>;
  }, [keyholderBillingEnabled]);

  // Keyholder Billing invoices are ALL returned, so we must
  // do the filtering here in the front end.
  function filterKeyholderInvoices(
    invoices: KeyholderInvoiceData[],
    givenFilters: InvoiceSearchFilters,
  ) {
    let filteredInvoices = invoices;
    if (givenFilters.status) {
      filteredInvoices = filteredInvoices.filter(
        invoice => invoice.status === givenFilters.status,
      );
    }
    if (givenFilters.createdBefore) {
      filteredInvoices = filteredInvoices.filter(
        invoice => new Date(invoice.createdAt) <= givenFilters.createdBefore,
      );
    }
    if (givenFilters.createdAfter) {
      filteredInvoices = filteredInvoices.filter(
        invoice => new Date(invoice.createdAt) >= givenFilters.createdAfter,
      );
    }
    return filteredInvoices;
  }

  useEffect(() => {
    // Grab the invoice data for a user utilizing Keyholder Billing
    // and send it to the table.
    if (
      featureEnabled('keyholderBillingFeature') &&
      billingProfile &&
      billingProfile.UserBillingNumber &&
      billingProfile.BoardBillingNumber
    ) {
      const fetchData = async () => {
        setLoading(true);
        // Go grab the user's invoices
        const invoices = await getKeyholderInvoices({
          supraOCN: billingProfile.BoardBillingNumber,
          billingCustomerNumber: billingProfile.UserBillingNumber,
        });
        // Apply any filters the user selected
        const filteredKeyholderInvoices = filterKeyholderInvoices(
          invoices,
          filters,
        );
        // Add a blank URL string to each object for now to satisfy the table
        const invoicesWithUrls = filteredKeyholderInvoices.map(invoice => ({
          ...invoice,
          url: '',
        }));
        // Send all the data to the table, finish loading
        setDataSource(invoicesWithUrls);
        setTotalCount(invoicesWithUrls.length);
        setEmail('');
        setLoading(false);
      };
      fetchData();
    }
    // Grab the invoice data for a legacy type user
    // and send it to the table.
    else {
      const fetchData = async () => {
        // Go grab the user's invoices
        setLoading(true);
        const { invoices, email: emailVal } = await getLegacyInvoices({
          orgId,
          userId,
          filters,
          first: 7,
        });
        // Send all the data to the table, finish loading
        setInvoicesData(invoices);
        setDataSource(invoices.edges.map(({ node }) => node));
        setTotalCount(invoices.totalCount);
        setEmail(emailVal);
        setLoading(false);
      };
      fetchData();
    }
  }, [billingProfile, filters, orgId, userId]);

  const handleSendEmail = useCallback(async () => {
    try {
      const startDate = filters.createdAfter
        ? `${moment(filters.createdAfter).format().slice(0, 19)}Z`
        : undefined;

      const endDate = filters.createdBefore
        ? `${moment(filters.createdBefore).format().slice(0, 19)}Z`
        : undefined;

      setSendingBillingHistoryEmail(true);
      await sendSupraBillingHistoryEmail({
        supraOCN: billingProfile.BoardBillingNumber,
        billingCustomerNumber: billingProfile.UserBillingNumber,
        startDate,
        endDate,
      });
      message.success('Email sent. Check your inbox.');
    } catch (e) {
      message.error(
        'Something went wrong. If this issue persists, please contact support.',
      );
    } finally {
      setSendingBillingHistoryEmail(false);
    }
  }, [billingProfile, filters]);

  return (
    <StyledCard
      title="Invoices"
      subtitle={
        Boolean(totalCount) && (
          <StyledSubtitle type="secondary">{totalCount} Results</StyledSubtitle>
        )
      }
      extra={
        <Extra
          openManageRecipients={() => setManageRecipientsOpen(true)}
          filters={filters}
          setFilters={setFilters}
          handleSendEmail={handleSendEmail}
          sendingBillingHistoryEmail={sendingBillingHistoryEmail}
        />
      }
      withContent={Boolean(totalCount)}
    >
      <>
        <Table
          dataSource={dataSource}
          rowKey="stripeInvoiceId"
          columns={columns}
          pagination={false}
          loading={loading}
          scroll={{ x: 'max-content', y: 385 }}
          onChange={(_, filtersValues) => {
            setFilters(prev => ({
              ...prev,
              status:
                filtersValues.status?.length === 1
                  ? (filtersValues.status[0] as InvoiceSearchFilters['status'])
                  : undefined,
            }));
          }}
          components={
            totalCount
              ? undefined
              : {
                  body: {
                    wrapper: () =>
                      loading ? (
                        <StyledNoContentWrapper>
                          <StyledNoContent>No invoices found</StyledNoContent>
                        </StyledNoContentWrapper>
                      ) : (
                        <></>
                      ),
                  },
                }
          }
        />
        {
          // If this is not Keyholder Billing data, use the pagination feature
          // so the user can view chunks of their invoices at a time. Otherwise,
          // display all the invoices at once.
          !featureEnabled('keyholderBillingFeature') &&
            invoicesData !== null && (
              <LoadMore
                loading={loading}
                hasNextPage={Boolean(invoicesData?.pageInfo.hasNextPage)}
                onLoad={async () => {
                  setLoading(true);
                  const { invoices } = await getLegacyInvoices({
                    orgId,
                    userId,
                    filters,
                    after: invoicesData?.pageInfo.endCursor ?? undefined,
                  });
                  setInvoicesData(prevData => ({
                    edges: (prevData?.edges || []).concat(invoices.edges),
                    pageInfo: invoices.pageInfo,
                    totalCount: invoices.totalCount,
                  }));
                  // @ts-ignore the (invoicesData !== null ) check ensures DataSource is the right type
                  setDataSource(prevDataSource => [
                    ...prevDataSource,
                    ...invoices.edges.map(({ node }) => node),
                  ]);
                  setLoading(false);
                }}
              />
            )
        }
      </>

      {!featureEnabled('keyholderBillingFeature') && (
        <ManageRecipientModal
          visible={isManageRecipientsOpen}
          onClose={() => setManageRecipientsOpen(false)}
          recipient={email}
          setEmail={setEmail}
          userId={userId}
          orgId={orgId}
        />
      )}
    </StyledCard>
  );
};

export default InvoicesCard;

export const StyledCard = styled(Card)<{ withContent?: boolean }>`
  ${({ withContent }) =>
    withContent &&
    `.ant-card-body {
    padding: 1px 0px;
  }`}
`;

export const StyledSubtitle = styled(Text)`
  margin-left: 15px;
  font-size: 12px;
  font-weight: 400;
  && {
    color: #595959;
  }
`;

export const StyledDateCell = styled.span`
  margin-left: 10px;
  display: block;
`;

const StyledStatus = styled.span<{ $status: string }>`
  color: ${({ $status }) =>
    $status.toLowerCase() === 'paid' ? 'black' : theme.getColor('danger')};
`;

export const StyledNoContentWrapper = styled.div`
  padding: 30px;
  width: 100%;
  display: flex;
  jsutify-content: center;
`;

export const StyledNoContent = styled(Text)`
  color: ${theme.getColor('textSecondary')};
`;
